import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { createPromptVersion } from "@/api/lookups";
import type { PromptVersionResponse, UUID } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const schema = z.object({
  text: z.string().min(1),
  activate: z.boolean().optional(),
});

type FormValues = z.infer<typeof schema>;

export interface PromptCreateFormProps {
  meetingTypeId: UUID;
  onCreated: (pv: PromptVersionResponse) => Promise<unknown> | void;
  /** Text of the currently active prompt version; used by "copy from current". */
  currentText?: string | null;
}

export function PromptCreateForm({
  meetingTypeId,
  onCreated,
  currentText,
}: PromptCreateFormProps) {
  const { t } = useTranslation();
  const create = useMutation({
    mutationFn: (v: FormValues) =>
      createPromptVersion({
        meeting_type_id: meetingTypeId,
        text: v.text,
        activate: v.activate ?? false,
      }),
    onSuccess: async (pv) => {
      await onCreated(pv);
    },
  });
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { text: "", activate: false },
  });
  const hasCurrent = typeof currentText === "string" && currentText.length > 0;
  const onCopyFromCurrent = () => {
    if (!hasCurrent) return;
    form.setValue("text", currentText as string, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };
  return (
    <form
      className="space-y-2 rounded-md border p-3"
      onSubmit={form.handleSubmit((v) => create.mutate(v))}
      data-testid="prompt-create-form"
    >
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor="prompt-text">{t("adminPrompts.newVersionText")}</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onCopyFromCurrent}
          disabled={!hasCurrent}
          title={
            hasCurrent
              ? undefined
              : t("adminPrompts.createForm.copyFromCurrentDisabledHint")
          }
          data-testid="prompt-copy-from-current"
        >
          {t("adminPrompts.createForm.copyFromCurrent")}
        </Button>
      </div>
      <textarea
        id="prompt-text"
        className="min-h-32 w-full rounded-md border bg-transparent px-3 py-2 text-sm"
        {...form.register("text")}
      />
      {form.formState.errors.text !== undefined ? (
        <p className="text-xs text-destructive">
          {t("adminPrompts.errors.textRequired")}
        </p>
      ) : null}
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          data-testid="prompt-activate"
          {...form.register("activate")}
        />
        {t("adminPrompts.activateCheckbox")}
      </label>
      {create.isError ? (
        <p className="text-xs text-destructive" role="alert">
          {t("adminPrompts.errors.saveFailed")}
        </p>
      ) : null}
      <Button type="submit" disabled={create.isPending}>
        {create.isPending ? t("common.saving") : t("adminPrompts.newVersion")}
      </Button>
    </form>
  );
}
